import SRConst from "@/helpers/Constants/SignalRConstants.js";

/**
 * Shared logic for eventual data: SignalR delivers external search results after local results.
 * Used by CustomerList, ArticleSearchMenuTextField, ListPane (AdminCustomerList, AdminArticles).
 * Provides mappers from sync payload to list item shape, merge helper, and timeout.
 */
export default {
  data() {
    return {
      eventualDataTimeoutId: null,
    };
  },

  methods: {
    clearEventualDataTimeout() {
      if (this.eventualDataTimeoutId) {
        clearTimeout(this.eventualDataTimeoutId);
        this.eventualDataTimeoutId = null;
      }
    },

    /**
     * Merge SignalR eventual-data payload into a list. Returns true if merged (caller should clear loading).
     * @param {object} args - SignalR event args with args.data.type, args.data.updated, args.data.new
     * @param {string} eventualDataType - SRConst.EventualDataType.Customer or SRConst.EventualDataType.Article
     * @param {Array} itemList - mutable list to merge into
     * @returns {boolean}
     */
    mergeEventualDataIntoList(args, eventualDataType, itemList) {
      const payload = args?.data;
      const type = payload?.type;
      if (!payload || type !== eventualDataType || !itemList) {
        return false;
      }
      this.clearEventualDataTimeout();
      const mapper = eventualDataType === SRConst.EventualDataType.Customer ? this.mapEventualCustomerToItem : this.mapEventualArticleToItem;
      const updated = payload.updated ?? [];
      const newItems = payload.new ?? [];

      updated.forEach((item) => {
        const id = item.id ?? item.Id;
        const index = itemList.findIndex(e => e.Id === id);
        const mapped = mapper(item);
        if (index >= 0) {
          //patch item if found
          const patched = { ...itemList[index], ...mapped };
          this.$set(itemList, index, patched);
        } else {
          //insert item if not found
          //we might not need to show items outside of the initial page that existed before.
          //skipping this does create a gap for updated items that did not fit the filters before, but do now.
          itemList.unshift(mapped);
        }
      });

      newItems.forEach((item) => {
        const id = item.id ?? item.Id;
        const existing = itemList.find(e => e.Id === id);
        if (!existing) {
          //insert new item if not found
          itemList.unshift(mapper(item));
        }
      });
      return true;
    },

    /**
     * Schedule a callback to run after 30s if eventual data never arrives (safety timeout).
     * @param {function} onTimeout - e.g. () => { this.loading = false }
     */
    scheduleEventualDataTimeout(onTimeout) {
      this.clearEventualDataTimeout();
      this.eventualDataTimeoutId = setTimeout(() => {
        this.eventualDataTimeoutId = null;
        onTimeout?.();
      }, 15000);
    },

    /** Map sync payload to customer, only fields that are relevant for render in the UI. */
    mapEventualCustomerToItem(item) {
      const id = item.id;
      return {
        Id: id,
        Name: item.name,
        IsActive: item.isActive,
        DefaultDeliveryAddress: {
          Id: "_" + id,
          CustomerId: id,
          Name: null,
          Street: item.deliveryAdressStreet,
          Zip: item.deliveryAdressZip,
          City: item.deliveryAdressCity,
          Country: item.deliveryAdressCountry,
        },
      };
    },

    /** Map sync payload to article, only fields that are relevant for render in the UI. */
    mapEventualArticleToItem(item) {
      return {
        Id: item.id ?? item.Id,
        ArticleDescription: item.articleDescription ?? item.ArticleDescription,
        ArticleNumber: item.articleNumber ?? item.ArticleNumber,
        IsActive: item.isActive ?? item.IsActive,
        //dont do price or storeitems as those need to be formatted by the be to account for external factors.
      };
    },
  },
};
